import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Mic2, Play, User, Globe, FileText, Building2, MessageSquare, Plus, Trash2,
  RefreshCw, Loader2, Send, Bot, ChevronRight, Link, BookOpen, HelpCircle,
  Package, Sparkles, AlertCircle, CheckCircle2, X, Volume2, ExternalLink, Copy, Palette, Phone, Share2
} from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const VOICE_OPTIONS = [
  { id: 'rachel', name: 'Rachel', desc: 'Warm & Professional', gender: 'Female' },
  { id: 'adam', name: 'Adam', desc: 'Authoritative & Clear', gender: 'Male' },
  { id: 'aria', name: 'Aria', desc: 'Friendly & Conversational', gender: 'Female' },
  { id: 'josh', name: 'Josh', desc: 'Calm & Reassuring', gender: 'Male' },
  { id: 'elli', name: 'Elli', desc: 'Energetic & Bright', gender: 'Female' },
  { id: 'sam', name: 'Sam', desc: 'Neutral & Professional', gender: 'Male' },
];

const INDUSTRIES = [
  'Electrical & Generator Services', 'HVAC & Mechanical', 'Plumbing', 'Construction & Contracting',
  'Real Estate', 'Healthcare & Medical', 'Legal Services', 'Financial Services',
  'Retail & E-Commerce', 'Food & Beverage', 'Hospitality & Hotels', 'Manufacturing',
  'Technology & Software', 'Education', 'Automotive', 'Beauty & Wellness',
  'Fitness & Gyms', 'Non-Profit', 'Government', 'Other'
];

const LANGUAGES = [
  { code: 'en', name: 'English' }, { code: 'es', name: 'Spanish' }, { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' }, { code: 'ar', name: 'Arabic' }, { code: 'tl', name: 'Tagalog' },
  { code: 'pt', name: 'Portuguese' }, { code: 'zh', name: 'Mandarin' }, { code: 'ja', name: 'Japanese' },
];

// ==================== SECTION COMPONENTS ====================

const IdentitySection = ({ config, onUpdate, onPreviewVoice }) => {
  const [local, setLocal] = useState({});

  useEffect(() => {
    setLocal({
      receptionist_name: config.receptionist_name || '',
      voice_id: config.voice_id || 'rachel',
      greeting_message: config.greeting_message || '',
      fallback_message: config.fallback_message || '',
      conversation_mode: config.conversation_mode || 'voice_and_chat',
    });
  }, [config]);

  const handleSave = () => onUpdate(local);

  return (
    <Card className="bg-white border-gray-border" data-testid="identity-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base sm:text-lg font-heading flex items-center gap-2">
          <User className="h-5 w-5 text-emerald-brand" />
          Receptionist Identity & Persona
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Receptionist Name</Label>
            <Input
              value={local.receptionist_name || ''}
              onChange={e => setLocal(p => ({ ...p, receptionist_name: e.target.value }))}
              placeholder="e.g., Holly"
              className="mt-1"
              data-testid="receptionist-name-input"
            />
            <p className="text-[10px] text-gray-400 mt-1">Used when the AI introduces itself</p>
          </div>
          <div>
            <Label>Conversation Mode</Label>
            <Select value={local.conversation_mode} onValueChange={v => setLocal(p => ({ ...p, conversation_mode: v }))}>
              <SelectTrigger className="mt-1" data-testid="conversation-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="voice_and_chat">Voice + Chat</SelectItem>
                <SelectItem value="voice_only">Voice Only</SelectItem>
                <SelectItem value="chat_only">Chat Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Receptionist Voice</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
            {VOICE_OPTIONS.map(v => (
              <button
                key={v.id}
                onClick={() => setLocal(p => ({ ...p, voice_id: v.id }))}
                className={cn(
                  "p-3 rounded-lg border text-left transition-all",
                  local.voice_id === v.id
                    ? "border-emerald-brand bg-emerald-brand/5 ring-1 ring-emerald-brand"
                    : "border-gray-200 hover:border-gray-300"
                )}
                data-testid={`voice-option-${v.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text-primary">{v.name}</span>
                  <button
                    onClick={e => { e.stopPropagation(); onPreviewVoice(v.id); }}
                    className="text-gray-400 hover:text-emerald-brand"
                  >
                    <Play className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">{v.desc} — {v.gender}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Greeting Message</Label>
          <Textarea
            value={local.greeting_message || ''}
            onChange={e => setLocal(p => ({ ...p, greeting_message: e.target.value }))}
            placeholder="Hi! I'm Holly, your virtual receptionist. How can I help you today?"
            className="mt-1 min-h-[80px]"
            data-testid="greeting-input"
          />
        </div>

        <div>
          <Label>Fallback Response</Label>
          <Textarea
            value={local.fallback_message || ''}
            onChange={e => setLocal(p => ({ ...p, fallback_message: e.target.value }))}
            placeholder="That's a great question! Let me connect you with our team..."
            className="mt-1 min-h-[60px]"
            data-testid="fallback-input"
          />
        </div>

        <Button onClick={handleSave} className="bg-emerald-brand hover:bg-emerald-hover w-full sm:w-auto" data-testid="save-identity-btn">
          Save Identity Settings
        </Button>
      </CardContent>
    </Card>
  );
};


const KnowledgeBaseSection = ({ stats, faqs, catalog, documents, context,
  onAddFaq, onDeleteFaq, onAddCatalog, onDeleteCatalog, onUpdateContext,
  onCrawlUrl, onDeleteDoc, onUploadFile, onRebuild, rebuilding }) => {

  const [faqForm, setFaqForm] = useState({ question: '', answer: '' });
  const [catalogForm, setCatalogForm] = useState({ name: '', description: '', price: '', duration: '' });
  const [urlInput, setUrlInput] = useState('');
  const [localCtx, setLocalCtx] = useState({});
  const [crawling, setCrawling] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => { setLocalCtx(context); }, [context]);

  const processFile = async (file) => {
    try {
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
      await onUploadFile(file.name, b64);
    } catch (err) {
      // onUploadFile handles its own toast errors
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target?.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      await processFile(file);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      await processFile(file);
    }
    setUploading(false);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  // Separate file-type documents from URL-type
  const fileDocuments = documents.filter(d => d.source_type === 'file');
  const urlDocuments = documents.filter(d => d.source_type !== 'file');

  return (
    <Tabs defaultValue="context" className="space-y-4">
      <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
        <TabsTrigger value="context" className="text-xs sm:text-sm whitespace-nowrap">Industry & Context</TabsTrigger>
        <TabsTrigger value="faqs" className="text-xs sm:text-sm whitespace-nowrap">FAQs ({faqs.length})</TabsTrigger>
        <TabsTrigger value="catalog" className="text-xs sm:text-sm whitespace-nowrap">Services ({catalog.length})</TabsTrigger>
        <TabsTrigger value="documents" className="text-xs sm:text-sm whitespace-nowrap">Documents ({fileDocuments.length})</TabsTrigger>
        <TabsTrigger value="urls" className="text-xs sm:text-sm whitespace-nowrap">URLs ({urlDocuments.length})</TabsTrigger>
      </TabsList>

      {/* Industry & Context Tab */}
      <TabsContent value="context" className="space-y-4">
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Building2 className="h-4 w-4 text-emerald-brand" />
              Industry & Business Context
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Primary Industry</Label>
                <Select value={localCtx.industry_primary || ''} onValueChange={v => setLocalCtx(p => ({ ...p, industry_primary: v }))}>
                  <SelectTrigger className="mt-1" data-testid="industry-primary-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Secondary Industry (optional)</Label>
                <Select value={localCtx.industry_secondary || ''} onValueChange={v => setLocalCtx(p => ({ ...p, industry_secondary: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Business Description</Label>
              <Textarea
                value={localCtx.business_description || ''}
                onChange={e => setLocalCtx(p => ({ ...p, business_description: e.target.value }))}
                placeholder="Write everything about your business: services, history, locations, team, certifications, pricing, hours..."
                className="mt-1 min-h-[200px]"
                data-testid="business-description-input"
              />
              <p className="text-[10px] text-gray-400 mt-1">{(localCtx.business_description || '').length} characters</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Service Areas</Label>
                <Textarea value={localCtx.service_areas || ''} onChange={e => setLocalCtx(p => ({ ...p, service_areas: e.target.value }))} placeholder="Areas you serve..." className="mt-1 min-h-[60px]" />
              </div>
              <div>
                <Label>Hours of Operation</Label>
                <Textarea value={localCtx.hours_of_operation || ''} onChange={e => setLocalCtx(p => ({ ...p, hours_of_operation: e.target.value }))} placeholder="Mon-Fri 8am-6pm..." className="mt-1 min-h-[60px]" />
              </div>
            </div>

            <div>
              <Label>What Makes You Better Than Competitors?</Label>
              <Textarea value={localCtx.competitor_differentiation || ''} onChange={e => setLocalCtx(p => ({ ...p, competitor_differentiation: e.target.value }))} placeholder="Your unique advantages..." className="mt-1 min-h-[60px]" />
            </div>

            <Button onClick={() => onUpdateContext(localCtx)} className="bg-emerald-brand hover:bg-emerald-hover w-full sm:w-auto" data-testid="save-context-btn">
              Save Business Context
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      {/* FAQs Tab */}
      <TabsContent value="faqs" className="space-y-4">
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-emerald-brand" />
              Frequently Asked Questions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-off-white rounded-lg space-y-3">
              <Input value={faqForm.question} onChange={e => setFaqForm(p => ({ ...p, question: e.target.value }))} placeholder="Question..." data-testid="faq-question-input" />
              <Textarea value={faqForm.answer} onChange={e => setFaqForm(p => ({ ...p, answer: e.target.value }))} placeholder="Answer..." className="min-h-[60px]" data-testid="faq-answer-input" />
              <Button
                size="sm"
                className="bg-emerald-brand hover:bg-emerald-hover gap-1"
                disabled={!faqForm.question.trim() || !faqForm.answer.trim()}
                onClick={() => { onAddFaq(faqForm); setFaqForm({ question: '', answer: '' }); }}
                data-testid="add-faq-btn"
              >
                <Plus className="h-3 w-3" /> Add FAQ
              </Button>
            </div>

            <div className="space-y-2">
              {faqs.map(faq => (
                <div key={faq.id} className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-text-primary">{faq.question}</p>
                      <p className="text-xs text-gray-medium mt-1">{faq.answer}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => onDeleteFaq(faq.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              {faqs.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No FAQs yet. Add your first one above.</p>}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Service Catalog Tab */}
      <TabsContent value="catalog" className="space-y-4">
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Package className="h-4 w-4 text-emerald-brand" />
              Service / Product Catalog
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-off-white rounded-lg space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input value={catalogForm.name} onChange={e => setCatalogForm(p => ({ ...p, name: e.target.value }))} placeholder="Service/Product Name" data-testid="catalog-name-input" />
                <Input value={catalogForm.price} onChange={e => setCatalogForm(p => ({ ...p, price: e.target.value }))} placeholder="Price (optional)" />
              </div>
              <Textarea value={catalogForm.description} onChange={e => setCatalogForm(p => ({ ...p, description: e.target.value }))} placeholder="Description..." className="min-h-[50px]" />
              <Button
                size="sm"
                className="bg-emerald-brand hover:bg-emerald-hover gap-1"
                disabled={!catalogForm.name.trim()}
                onClick={() => { onAddCatalog(catalogForm); setCatalogForm({ name: '', description: '', price: '', duration: '' }); }}
                data-testid="add-catalog-btn"
              >
                <Plus className="h-3 w-3" /> Add Item
              </Button>
            </div>

            <div className="space-y-2">
              {catalog.map(item => (
                <div key={item.id} className="p-3 border border-gray-200 rounded-lg flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-text-primary">{item.name}</span>
                      {item.price && <Badge variant="outline" className="text-[10px]">{item.price}</Badge>}
                    </div>
                    {item.description && <p className="text-xs text-gray-medium mt-0.5 truncate">{item.description}</p>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => onDeleteCatalog(item.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {catalog.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No services/products yet.</p>}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Documents Tab */}
      <TabsContent value="documents" className="space-y-4">
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <FileText className="h-4 w-4 text-emerald-brand" />
              Upload Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={cn(
                "border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer",
                dragOver ? "border-emerald-brand bg-emerald-50" : "border-gray-200 hover:border-emerald-brand"
              )}
              onClick={() => !uploading && fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDragEnter={handleDragOver}
              data-testid="file-upload-area"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xlsx,.xls,.pptx,.ppt,.txt,.csv,.md,.rtf"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-upload-input"
              />
              {uploading ? (
                <div className="space-y-2">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-emerald-brand" />
                  <p className="text-sm text-gray-600">Processing & indexing files...</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <FileText className="h-8 w-8 mx-auto text-gray-300" />
                  <p className="text-sm text-gray-600">Click to upload or drag & drop</p>
                  <p className="text-[10px] text-gray-400">PDF, Word, Excel, PowerPoint, TXT, CSV (max 20MB each)</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {fileDocuments.map(doc => (
                <div key={doc.id} className="p-3 border border-gray-200 rounded-lg flex items-center justify-between gap-2" data-testid={`file-doc-${doc.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-text-primary truncate">{doc.file_name || doc.source_label}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <Badge className={cn("text-[10px]",
                        doc.status === 'indexed' ? 'bg-emerald-brand' : doc.status === 'error' ? 'bg-red-500' : 'bg-amber-500'
                      )}>
                        {doc.status}
                      </Badge>
                      {doc.chunk_count > 0 && <span className="text-[10px] text-gray-400">{doc.chunk_count} chunks</span>}
                      {doc.file_size && <span className="text-[10px] text-gray-400">{(doc.file_size / 1024).toFixed(0)}KB</span>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => onDeleteDoc(doc.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {fileDocuments.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No files uploaded yet. Upload PDFs, spreadsheets, or presentations to build the knowledge base.</p>}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* URLs Tab */}
      <TabsContent value="urls" className="space-y-4">
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Globe className="h-4 w-4 text-emerald-brand" />
              Website & URLs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                placeholder="https://yourwebsite.com"
                className="flex-1"
                data-testid="url-input"
              />
              <Button
                className="bg-emerald-brand hover:bg-emerald-hover gap-1 w-full sm:w-auto"
                disabled={!urlInput.trim() || crawling}
                onClick={async () => {
                  setCrawling(true);
                  await onCrawlUrl(urlInput);
                  setUrlInput('');
                  setCrawling(false);
                }}
                data-testid="crawl-url-btn"
              >
                {crawling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link className="h-4 w-4" />}
                {crawling ? 'Crawling...' : 'Add & Index'}
              </Button>
            </div>

            <div className="space-y-2">
              {urlDocuments.map(doc => (
                <div key={doc.id} className="p-3 border border-gray-200 rounded-lg flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-text-primary truncate">{doc.source_label || doc.source_url}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <Badge className={cn("text-[10px]",
                        doc.status === 'indexed' ? 'bg-emerald-brand' : doc.status === 'error' ? 'bg-red-500' : 'bg-amber-500'
                      )}>
                        {doc.status}
                      </Badge>
                      {doc.chunk_count > 0 && <span className="text-[10px] text-gray-400">{doc.chunk_count} chunks</span>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => onDeleteDoc(doc.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {urlDocuments.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No URLs indexed yet. Add a website URL above.</p>}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Knowledge Health + Actions */}
      <Card className="bg-white border-gray-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-center">
                <div className={cn(
                  "text-2xl sm:text-3xl font-bold font-heading",
                  stats.coverage_score >= 86 ? 'text-emerald-brand' : stats.coverage_score >= 61 ? 'text-green-500' : stats.coverage_score >= 31 ? 'text-amber-500' : 'text-red-500'
                )}>
                  {stats.coverage_score}
                </div>
                <p className="text-[10px] text-gray-400">Coverage</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-medium">
                <span>{stats.total_vectors} vectors</span>
                <span>{stats.total_documents} pages</span>
                <span>{stats.total_faqs} FAQs</span>
                <span>{stats.total_catalog_items} services</span>
              </div>
            </div>
            <Button variant="outline" size="sm" className="gap-1" disabled={rebuilding} onClick={onRebuild} data-testid="rebuild-kb-btn">
              {rebuilding ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Rebuild KB
            </Button>
          </div>
        </CardContent>
      </Card>
    </Tabs>
  );
};


// ==================== TEST AI CHAT MODAL ====================

const TestAIModal = ({ open, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/voice/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ question: input, conversation_history: messages }),
      });
      const data = await res.json();
      if (data.answer) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.answer,
          sources: data.sources || [],
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col" data-testid="test-ai-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-emerald-brand" />
            Test Your AI Receptionist
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 py-4 min-h-[300px]">
          {messages.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">
              Ask a question to test your knowledge base.
            </p>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={cn("flex", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
              <div className={cn(
                "max-w-[80%] rounded-xl px-3 py-2",
                msg.role === 'user' ? 'bg-emerald-brand text-white' : 'bg-gray-100 text-text-primary'
              )}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.sources?.length > 0 && (
                  <div className="mt-1.5 border-t border-gray-200 pt-1">
                    {msg.sources.map((s, j) => (
                      <p key={j} className="text-[9px] text-gray-400">Source: {s.label}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-xl px-4 py-3">
                <Loader2 className="h-4 w-4 animate-spin text-emerald-brand" />
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 pt-2 border-t">
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Ask a question..."
            className="flex-1"
            data-testid="test-ai-input"
          />
          <Button onClick={sendMessage} disabled={loading || !input.trim()} className="bg-emerald-brand hover:bg-emerald-hover" data-testid="test-ai-send-btn">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ==================== PUBLIC PAGE SETTINGS ====================

const PublicPageSection = ({ config, onUpdate }) => {
  const [local, setLocal] = useState({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLocal({
      tenant_slug: config.tenant_slug || '',
      primary_color: config.primary_color || '#10B981',
      secondary_color: config.secondary_color || '#1F2937',
      text_color: config.text_color || '#111827',
      background_style: config.background_style || 'solid',
      background_value: config.background_value || '#FFFFFF',
      welcome_headline: config.welcome_headline || '',
      welcome_subheadline: config.welcome_subheadline || '',
      show_logo: config.show_logo !== false,
      show_powered_by: config.show_powered_by !== false,
      lead_capture_enabled: config.lead_capture_enabled !== false,
      require_contact_before_chat: config.require_contact_before_chat || false,
    });
  }, [config]);

  const publicUrl = `${window.location.origin}/voice/${local.tenant_slug || 'your-slug'}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="bg-white border-gray-border" data-testid="public-page-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base sm:text-lg font-heading flex items-center gap-2">
          <Palette className="h-5 w-5 text-emerald-brand" />
          Public Page & Branding
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Public URL */}
        <div>
          <Label>Your Voice Page URL</Label>
          <div className="flex flex-col sm:flex-row gap-2 mt-1">
            <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-off-white rounded-lg border border-gray-200 min-w-0 overflow-hidden">
              <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-600 truncate" data-testid="public-url-display">{publicUrl}</span>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button variant="outline" size="sm" className="gap-1 flex-shrink-0" onClick={copyUrl} data-testid="copy-url-btn">
                <Copy className="h-3 w-3" />
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button variant="outline" size="sm" className="gap-1 flex-shrink-0" onClick={() => window.open(publicUrl, '_blank')} data-testid="preview-page-btn">
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Slug */}
        <div>
          <Label>URL Slug</Label>
          <Input
            value={local.tenant_slug || ''}
            onChange={e => setLocal(p => ({ ...p, tenant_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
            placeholder="your-company-name"
            className="mt-1"
            data-testid="slug-input"
          />
          <p className="text-[10px] text-gray-400 mt-1">Only lowercase letters, numbers, and hyphens</p>
        </div>

        {/* Colors */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>Primary Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={local.primary_color || '#10B981'} onChange={e => setLocal(p => ({ ...p, primary_color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border-0" />
              <Input value={local.primary_color || ''} onChange={e => setLocal(p => ({ ...p, primary_color: e.target.value }))} className="flex-1" data-testid="primary-color-input" />
            </div>
          </div>
          <div>
            <Label>Secondary Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={local.secondary_color || '#1F2937'} onChange={e => setLocal(p => ({ ...p, secondary_color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border-0" />
              <Input value={local.secondary_color || ''} onChange={e => setLocal(p => ({ ...p, secondary_color: e.target.value }))} className="flex-1" />
            </div>
          </div>
          <div>
            <Label>Background</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={local.background_value || '#FFFFFF'} onChange={e => setLocal(p => ({ ...p, background_value: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border-0" />
              <Input value={local.background_value || ''} onChange={e => setLocal(p => ({ ...p, background_value: e.target.value }))} className="flex-1" />
            </div>
          </div>
        </div>

        {/* Welcome text */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Welcome Headline</Label>
            <Input value={local.welcome_headline || ''} onChange={e => setLocal(p => ({ ...p, welcome_headline: e.target.value }))} placeholder="Talk to our AI Assistant" className="mt-1" data-testid="welcome-headline-input" />
          </div>
          <div>
            <Label>Welcome Subheadline</Label>
            <Input value={local.welcome_subheadline || ''} onChange={e => setLocal(p => ({ ...p, welcome_subheadline: e.target.value }))} placeholder="Tap the mic or type below" className="mt-1" />
          </div>
        </div>

        {/* Toggles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-off-white rounded-lg">
            <div>
              <p className="text-sm text-text-primary">Lead Capture</p>
              <p className="text-[10px] text-gray-400">Collect visitor contact info</p>
            </div>
            <Switch checked={local.lead_capture_enabled} onCheckedChange={v => setLocal(p => ({ ...p, lead_capture_enabled: v }))} data-testid="lead-capture-toggle" />
          </div>
          <div className="flex items-center justify-between p-3 bg-off-white rounded-lg">
            <div>
              <p className="text-sm text-text-primary">Require Contact</p>
              <p className="text-[10px] text-gray-400">Must provide info before chat</p>
            </div>
            <Switch checked={local.require_contact_before_chat} onCheckedChange={v => setLocal(p => ({ ...p, require_contact_before_chat: v }))} data-testid="require-contact-toggle" />
          </div>
          <div className="flex items-center justify-between p-3 bg-off-white rounded-lg">
            <div>
              <p className="text-sm text-text-primary">Show Logo</p>
              <p className="text-[10px] text-gray-400">Display your logo on the page</p>
            </div>
            <Switch checked={local.show_logo} onCheckedChange={v => setLocal(p => ({ ...p, show_logo: v }))} />
          </div>
          <div className="flex items-center justify-between p-3 bg-off-white rounded-lg">
            <div>
              <p className="text-sm text-text-primary">Powered By</p>
              <p className="text-[10px] text-gray-400">Show "Powered by Konnect"</p>
            </div>
            <Switch checked={local.show_powered_by} onCheckedChange={v => setLocal(p => ({ ...p, show_powered_by: v }))} />
          </div>
        </div>

        <Button onClick={() => onUpdate(local)} className="bg-emerald-brand hover:bg-emerald-hover w-full sm:w-auto" data-testid="save-public-page-btn">
          Save Public Page Settings
        </Button>
      </CardContent>
    </Card>
  );
};


// ==================== UPGRADE GATE ====================

const UpgradeGate = () => (
  <div className="flex items-center justify-center min-h-[60vh]" data-testid="enterprise-gate">
    <Card className="bg-white border-gray-border max-w-md mx-auto">
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-emerald-brand/10 flex items-center justify-center mx-auto mb-4">
          <Mic2 className="h-8 w-8 text-emerald-brand" />
        </div>
        <h2 className="text-xl font-heading font-bold text-text-primary mb-2">Voice Receptionist</h2>
        <p className="text-sm text-gray-medium mb-4">
          Get a 24/7 AI receptionist that knows everything about your business. Powered by your own private knowledge base.
        </p>
        <div className="space-y-2 text-left mb-6">
          {['AI-powered voice & chat receptionist', 'Private knowledge base (RAG)', 'Custom branding & domain', 'Lead capture & CRM integration', 'Conversation analytics'].map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle2 className="h-4 w-4 text-emerald-brand flex-shrink-0" />
              {f}
            </div>
          ))}
        </div>
        <Badge className="bg-purple-500 text-white mb-4">Enterprise Feature</Badge>
        <Button className="bg-emerald-brand hover:bg-emerald-hover w-full gap-2" data-testid="upgrade-btn">
          <Sparkles className="h-4 w-4" />
          Upgrade to Enterprise
        </Button>
      </CardContent>
    </Card>
  </div>
);


// ==================== MAIN PAGE ====================

export const VoiceReceptionistPage = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState({});
  const [stats, setStats] = useState({ coverage_score: 0, total_vectors: 0, total_documents: 0, total_faqs: 0, total_catalog_items: 0 });
  const [faqs, setFaqs] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [context, setContext] = useState({});
  const [loading, setLoading] = useState(true);
  const [testOpen, setTestOpen] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEnterprise = user?.plan === 'enterprise' || user?.role === 'super_admin';

  const authHeaders = () => {
    const token = localStorage.getItem('konnect_access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchData = useCallback(async () => {
    try {
      const hdrs = { 'Authorization': `Bearer ${localStorage.getItem('konnect_access_token') || ''}` };
      const [configRes, statsRes, faqsRes, catalogRes, docsRes, ctxRes] = await Promise.all([
        fetch(`${API}/api/voice/config`, { headers: hdrs }),
        fetch(`${API}/api/voice/knowledge/stats`, { headers: hdrs }),
        fetch(`${API}/api/voice/knowledge/faqs`, { headers: hdrs }),
        fetch(`${API}/api/voice/knowledge/catalog`, { headers: hdrs }),
        fetch(`${API}/api/voice/knowledge/documents`, { headers: hdrs }),
        fetch(`${API}/api/voice/knowledge/context`, { headers: hdrs }),
      ]);
      const [configData, statsData, faqsData, catalogData, docsData, ctxData] = await Promise.all([
        configRes.json(), statsRes.json(), faqsRes.json(), catalogRes.json(), docsRes.json(), ctxRes.json()
      ]);
      setConfig(configData);
      setStats(statsData);
      setFaqs(faqsData.faqs || []);
      setCatalog(catalogData.catalog || []);
      setDocuments(docsData.documents || []);
      setContext(ctxData);
    } catch (err) {
      console.error('Failed to load voice data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isEnterprise) fetchData(); else setLoading(false); }, [isEnterprise, fetchData]);

  if (!isEnterprise) return <UpgradeGate />;
  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="h-8 w-8 animate-spin text-emerald-brand" /></div>;

  const apiCall = async (url, method = 'GET', body = null) => {
    const opts = { method, headers: { 'Content-Type': 'application/json', ...authHeaders() } };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(`${API}${url}`, opts);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Request failed' }));
      toast.error(err.detail || 'Something went wrong');
      throw new Error(err.detail);
    }
    return res.json();
  };

  const handleUpdateConfig = async (data) => {
    setSaving(true);
    try {
      await apiCall('/api/voice/config', 'PUT', data);
      toast.success('Settings saved successfully');
      await fetchData();
    } catch { /* error already toasted by apiCall */ }
    setSaving(false);
  };

  const handlePreviewVoice = async (voiceId) => {
    try {
      const res = await fetch(`${API}/api/voice/preview-voice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ voice_id: voiceId }),
      });
      if (res.ok && res.headers.get('content-type')?.includes('audio')) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => URL.revokeObjectURL(url);
        audio.play();
      }
    } catch (err) {
      console.error('Voice preview failed:', err);
    }
  };

  return (
    <div className="space-y-6" data-testid="voice-receptionist-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold text-text-primary flex items-center gap-2">
            <Mic2 className="h-6 w-6 sm:h-7 sm:w-7 text-emerald-brand" />
            Voice Receptionist<PageInfoTooltip pageKey="voice-receptionist" />
          </h1>
          <p className="text-sm text-gray-medium">Brand Voice Knowledge Center — Configure your AI receptionist</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => setTestOpen(true)} data-testid="test-ai-btn">
            <MessageSquare className="h-4 w-4" />
            Test AI
          </Button>
          <div className="flex items-center gap-2">
            <Switch
              checked={config.is_active || false}
              onCheckedChange={v => handleUpdateConfig({ is_active: v })}
              data-testid="receptionist-active-toggle"
            />
            <Label className="text-sm">{config.is_active ? 'Live' : 'Off'}</Label>
          </div>
        </div>
      </div>

      {/* Floating Widget Toggle */}
      <Card className="bg-white border-gray-border" data-testid="widget-toggle-card">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-brand/80 flex items-center justify-center">
                <Phone className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Floating Call Widget</p>
                <p className="text-[10px] text-gray-400">Show a floating phone button for quick access to the AI receptionist</p>
              </div>
            </div>
            <Switch
              checked={config.widget_enabled || false}
              onCheckedChange={v => handleUpdateConfig({ widget_enabled: v })}
              data-testid="widget-enabled-toggle"
            />
          </div>

          {/* Share Widget Link */}
          {config.tenant_slug && (() => {
            const publicUrl = `${window.location.origin}/voice/${config.tenant_slug}`;
            const shareText = `Chat with our AI assistant! ${publicUrl}`;
            const encodedUrl = encodeURIComponent(publicUrl);
            const encodedText = encodeURIComponent(shareText);
            const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied!'); };
            return (
              <div className="border-t border-gray-100 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-text-primary flex items-center gap-1.5"><Share2 className="h-3.5 w-3.5" /> Share Receptionist Link</p>
                  <button onClick={copyLink} className="flex items-center gap-1 text-[10px] text-emerald-brand hover:underline" data-testid="copy-widget-link"><Copy className="h-3 w-3" />Copy Link</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <a href={`https://wa.me/?text=${encodedText}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1.5 rounded-lg bg-[#25D366]/10 text-[#25D366] text-[11px] font-medium hover:bg-[#25D366]/20 transition-colors" data-testid="share-whatsapp">WhatsApp</a>
                  <a href={`https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=0&redirect_uri=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1.5 rounded-lg bg-[#0084FF]/10 text-[#0084FF] text-[11px] font-medium hover:bg-[#0084FF]/20 transition-colors" data-testid="share-messenger">Messenger</a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1.5 rounded-lg bg-[#1877F2]/10 text-[#1877F2] text-[11px] font-medium hover:bg-[#1877F2]/20 transition-colors" data-testid="share-facebook">Facebook</a>
                  <a href={`https://twitter.com/intent/tweet?text=${encodedText}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1.5 rounded-lg bg-black/5 text-gray-700 text-[11px] font-medium hover:bg-black/10 transition-colors" data-testid="share-x">X / Twitter</a>
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1.5 rounded-lg bg-[#0A66C2]/10 text-[#0A66C2] text-[11px] font-medium hover:bg-[#0A66C2]/20 transition-colors" data-testid="share-linkedin">LinkedIn</a>
                  <a href={`mailto:?subject=Chat with our AI Assistant&body=${encodedText}`} className="px-2.5 py-1.5 rounded-lg bg-orange-500/10 text-orange-600 text-[11px] font-medium hover:bg-orange-500/20 transition-colors" data-testid="share-email">Email</a>
                  <a href={`sms:?body=${encodedText}`} className="px-2.5 py-1.5 rounded-lg bg-emerald-brand/10 text-emerald-brand text-[11px] font-medium hover:bg-emerald-brand/20 transition-colors" data-testid="share-sms">SMS</a>
                  <a href={`https://telegram.me/share/url?url=${encodedUrl}&text=${encodeURIComponent('Chat with our AI assistant!')}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1.5 rounded-lg bg-[#0088CC]/10 text-[#0088CC] text-[11px] font-medium hover:bg-[#0088CC]/20 transition-colors" data-testid="share-telegram">Telegram</a>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 truncate">{publicUrl}</p>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Knowledge Coverage Score */}
      <Card className="bg-white border-gray-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke={stats.coverage_score >= 86 ? '#10B981' : stats.coverage_score >= 61 ? '#22C55E' : stats.coverage_score >= 31 ? '#F59E0B' : '#EF4444'}
                  strokeWidth="3"
                  strokeDasharray={`${stats.coverage_score}, 100`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm sm:text-lg font-bold text-text-primary">{stats.coverage_score}</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary">
                {stats.coverage_score >= 86 ? 'Expert' : stats.coverage_score >= 61 ? 'Strong' : stats.coverage_score >= 31 ? 'Good' : 'Basic'}
                {' '}Knowledge Coverage
              </p>
              <p className="text-xs text-gray-400">
                {stats.coverage_score >= 86
                  ? 'Your AI can answer virtually any question about your business.'
                  : stats.coverage_score >= 61
                    ? 'Your AI has solid knowledge. Add more content to reach Expert level.'
                    : stats.coverage_score >= 31
                      ? 'Your AI can answer common questions. Add more details.'
                      : 'Your AI has limited knowledge. Add more content.'}
              </p>
              <Progress value={stats.coverage_score} className="mt-2 h-1.5" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Identity Section */}
      <IdentitySection config={config} onUpdate={handleUpdateConfig} onPreviewVoice={handlePreviewVoice} />

      {/* Public Page & Branding */}
      <PublicPageSection config={config} onUpdate={handleUpdateConfig} />

      {/* Knowledge Base Section */}
      <KnowledgeBaseSection
        stats={stats}
        faqs={faqs}
        catalog={catalog}
        documents={documents}
        context={context}
        onAddFaq={async (data) => { await apiCall('/api/voice/knowledge/faqs', 'POST', data); toast.success('FAQ added'); fetchData(); }}
        onDeleteFaq={async (id) => { await apiCall(`/api/voice/knowledge/faqs/${id}`, 'DELETE'); toast.success('FAQ deleted'); fetchData(); }}
        onAddCatalog={async (data) => { await apiCall('/api/voice/knowledge/catalog', 'POST', data); toast.success('Service added'); fetchData(); }}
        onDeleteCatalog={async (id) => { await apiCall(`/api/voice/knowledge/catalog/${id}`, 'DELETE'); toast.success('Service deleted'); fetchData(); }}
        onUpdateContext={async (data) => { setSaving(true); await apiCall('/api/voice/knowledge/context', 'PUT', data); toast.success('Business context saved'); await fetchData(); setSaving(false); }}
        onCrawlUrl={async (url) => { await apiCall('/api/voice/knowledge/crawl-url', 'POST', { url }); toast.success('URL crawled & indexed'); fetchData(); }}
        onDeleteDoc={async (id) => { await apiCall(`/api/voice/knowledge/documents/${id}`, 'DELETE'); toast.success('Document deleted'); fetchData(); }}
        onUploadFile={async (filename, fileDataB64) => {
          const CHUNK_SIZE = 200 * 1024; // 200KB chunks — small enough for any WAF
          const hdrs = { 'Content-Type': 'application/json', ...authHeaders() };
          const url = `${API}/api/voice/knowledge/upload-file`;
          try {
            if (fileDataB64.length <= CHUNK_SIZE) {
              // Small file — single request
              const res = await fetch(url, { method: 'POST', headers: hdrs,
                body: JSON.stringify({ filename, file_data: fileDataB64 }) });
              const data = await res.json().catch(() => null);
              if (!res.ok) { toast.error((data?.detail) || `Upload failed (${res.status})`); return; }
              toast.success(`${filename} uploaded successfully`);
            } else {
              // Large file — chunked upload
              const totalChunks = Math.ceil(fileDataB64.length / CHUNK_SIZE);
              // Step 1: Init
              const initRes = await fetch(url, { method: 'POST', headers: hdrs,
                body: JSON.stringify({ mode: 'init', filename, total_chunks: totalChunks }) });
              const initData = await initRes.json().catch(() => null);
              if (!initRes.ok || !initData?.upload_id) { toast.error(initData?.detail || 'Upload init failed'); return; }
              const uploadId = initData.upload_id;
              // Step 2: Send chunks
              for (let i = 0; i < totalChunks; i++) {
                const chunk = fileDataB64.substring(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
                const chunkRes = await fetch(url, { method: 'POST', headers: hdrs,
                  body: JSON.stringify({ mode: 'chunk', upload_id: uploadId, chunk_index: i, data: chunk }) });
                if (!chunkRes.ok) { const e = await chunkRes.json().catch(() => null); toast.error(e?.detail || `Chunk ${i+1} failed`); return; }
              }
              // Step 3: Complete
              const compRes = await fetch(url, { method: 'POST', headers: hdrs,
                body: JSON.stringify({ mode: 'complete', upload_id: uploadId }) });
              const compData = await compRes.json().catch(async () => {
                const txt = await compRes.text().catch(() => '');
                return { detail: txt || `HTTP ${compRes.status}` };
              });
              if (!compRes.ok) { toast.error(compData?.detail || `Processing failed (HTTP ${compRes.status})`); return; }
              toast.success(`${filename} uploaded successfully`);
            }
            fetchData();
          } catch (err) {
            toast.error(`Upload error: ${err.message || 'Network error'}`);
          }
        }}
        onRebuild={async () => { setRebuilding(true); await apiCall('/api/voice/knowledge/rebuild', 'POST'); toast.success('Knowledge base rebuilt'); await fetchData(); setRebuilding(false); }}
        rebuilding={rebuilding}
      />

      {/* Test AI Modal */}
      <TestAIModal open={testOpen} onClose={() => setTestOpen(false)} />
    </div>
  );
};

export default VoiceReceptionistPage;
